import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { addDays, endOfMonth, endOfWeek, format, isWithinInterval, parseISO, startOfMonth, startOfWeek } from 'date-fns'
import { Icon, Select, Spinner, Typography } from '@telavita-core/react-design-kit'
import ModalGeneric from '../ModalGeneric'
import { getProfessionalAvailability } from 'services/professionalAvailabilityService'
import { useGetProfessionalProfile } from 'views/NpsSchedule/hooks/useGetProfessionalProfile'
import { UserData } from 'models/Auth'

import * as S from './styles'
import { ExceptionDateAsText } from './styles'

const RESTRICTION_WEEK_OPTIONS = { weekStartsOn: 0 as const }

type DateOption = { id: string; label: string }

type RestrictionSelection = {
  dateKey: string | null
  hour: string | null
}

function getDateOptionsInRestrictionWeek(
  allOptions: DateOption[],
  restrictionDate: Date
): DateOption[] {
  const interval = {
    start: startOfWeek(restrictionDate, RESTRICTION_WEEK_OPTIONS),
    end: endOfWeek(restrictionDate, RESTRICTION_WEEK_OPTIONS),
  }
  return allOptions.filter((option) => {
    const day = parseISO(`${option.id}T12:00:00`)
    return isWithinInterval(day, interval)
  })
}

function getRestrictionWeekDateKeys(restrictionDate: Date): string[] {
  const weekStartDate = startOfWeek(restrictionDate, RESTRICTION_WEEK_OPTIONS)
  return Array.from({ length: 7 }, (_, dayIndex) => (
    format(addDays(weekStartDate, dayIndex), 'yyyy-MM-dd')
  ))
}

interface Props {
  restrictions?: Array<Date | string>
  patientUsername?: string
  professionalUsername?: string
  scheduleStartDate?: string
  onSelectNonRecurrentReservationDate?: (date: string | null, index: number) => void
  onConfirm?: () => void | Promise<void>
  confirmButtonText?: string
  confirmDisabled?: boolean
  onClose: () => void
}

export function PatientCreatingReservationRestrictionsModal({
  onClose,
  restrictions = [],
  patientUsername = '',
  professionalUsername = '',
  scheduleStartDate = '',
  onSelectNonRecurrentReservationDate,
  onConfirm,
  confirmButtonText = 'Concluir',
  confirmDisabled = false,
}: Props): JSX.Element {
  const parsedRestrictions = useMemo(() => (
    restrictions
      .map((restriction) => (restriction instanceof Date ? restriction : new Date(restriction)))
      .filter((restriction) => !Number.isNaN(restriction.getTime()))
  ), [restrictions])
  const hasRestrictions = parsedRestrictions.length > 0
  const planCode = useSelector((state: UserData) => state.auth.planSelected?.partner_plan_code)

  const {
    professionalProfile,
    professionalProfileLoading,
  } = useGetProfessionalProfile(professionalUsername)

  const [selectedOptionsByRestriction, setSelectedOptionsByRestriction] = useState<RestrictionSelection[]>([])

  const { data: availabilityByDay = {}, isFetching } = useQuery({
    queryKey: [
      'patient-restrictions-modal-availabilities',
      professionalProfile?.friendlyUrl,
      patientUsername,
      planCode,
      professionalProfile?.productCode,
      parsedRestrictions.map((date) => date.toISOString()),
    ],
    enabled: Boolean(
      hasRestrictions &&
      patientUsername &&
      professionalProfile?.friendlyUrl &&
      professionalProfile?.productCode &&
      planCode
    ),
    queryFn: async () => {
      const sortedRestrictions = [...parsedRestrictions].sort((a, b) => a.getTime() - b.getTime())
      const minRestrictionDate = sortedRestrictions[0]
      const maxRestrictionDate = sortedRestrictions[sortedRestrictions.length - 1]

      const response = await getProfessionalAvailability({
        slug: professionalProfile.friendlyUrl,
        username: patientUsername,
        profileRole: 'PSIC',
        startDate: startOfMonth(minRestrictionDate).toISOString(),
        endDate: endOfMonth(maxRestrictionDate).toISOString(),
        planCode,
        productCode: professionalProfile.productCode,
        groupBy: 'date',
        isParticular: false
      })
      const mappedAvailabilityByDay = response.availabilities.reduce((accumulator, availability) => {
        const dateKey = format(availability.day, 'yyyy-MM-dd')
        accumulator[dateKey] = availability.options.map((option) => (
          format(option.startDate, "yyyy-MM-dd'T'HH:mm:ss")
        ))
        return accumulator
      }, {} as Record<string, string[]>)
      return mappedAvailabilityByDay
    },
  })

  const dateOptions = useMemo(() => (
    Object.keys(availabilityByDay).map((dateKey) => ({
      id: dateKey,
      label: format(new Date(`${dateKey}T00:00:00`), 'dd/MM'),
    }))
  ), [availabilityByDay])

  const isLoadingModalAvailabilities =
    hasRestrictions && (professionalProfileLoading || isFetching)

  const hasNoAvailableHoursByRestriction = useMemo(() => (
    parsedRestrictions.map((restrictionDate) => {
      if (isLoadingModalAvailabilities) return false
      const restrictionWeekDateKeys = getRestrictionWeekDateKeys(restrictionDate)
      return restrictionWeekDateKeys.every((dateKey) => {
        const dailyHours = availabilityByDay[dateKey] || []
        return dailyHours.length === 0
      })
    })
  ), [availabilityByDay, isLoadingModalAvailabilities, parsedRestrictions])

  const effectiveConfirmDisabled = isLoadingModalAvailabilities || confirmDisabled

  function handlePrimaryButtonClick() {
    if (onConfirm) {
      void Promise.resolve(onConfirm())
    } else {
      onClose()
    }
  }

  useEffect(() => {
    if (dateOptions.length === 0 || parsedRestrictions.length === 0) return
    setSelectedOptionsByRestriction((previousState) => {
      let hasChanged = previousState.length !== parsedRestrictions.length
      const nextState = parsedRestrictions.map((restrictionDate, index) => {
        const previousSelection = previousState[index]
        if (previousSelection?.dateKey) {
          const weekOptions = getDateOptionsInRestrictionWeek(dateOptions, restrictionDate)
          const stillValid = weekOptions.some((o) => o.id === previousSelection.dateKey)
          if (stillValid) return previousSelection
        }
        const weekDateOptions = getDateOptionsInRestrictionWeek(dateOptions, restrictionDate)
        const restrictionDateKey = format(restrictionDate, 'yyyy-MM-dd')
        const hasRestrictionDateInWeek = weekDateOptions.some((option) => option.id === restrictionDateKey)
        const hasNoAvailableHoursInWeek = hasNoAvailableHoursByRestriction[index]
        hasChanged = true
        return {
          dateKey: hasNoAvailableHoursInWeek
            ? restrictionDateKey
            : hasRestrictionDateInWeek
            ? restrictionDateKey
            : (weekDateOptions[0] ? String(weekDateOptions[0].id) : null),
          hour: null,
        }
      })

      if (!hasChanged) {
        return previousState
      }

      return nextState
    })
  }, [dateOptions, hasNoAvailableHoursByRestriction, parsedRestrictions])

  function handleSelectDate(dateKey: string, restrictionIndex: number) {
    setSelectedOptionsByRestriction((previousState) => {
      const nextState = [...previousState]
      nextState[restrictionIndex] = {
        dateKey,
        hour: null,
      }
      return nextState
    })
    if (onSelectNonRecurrentReservationDate) {
      onSelectNonRecurrentReservationDate(null, restrictionIndex)
    }
  }

  function handleSelectHour(hour: string, restrictionIndex: number) {
    setSelectedOptionsByRestriction((previousState) => {
      const nextState = [...previousState]
      nextState[restrictionIndex] = {
        dateKey: nextState[restrictionIndex]?.dateKey || null,
        hour,
      }
      return nextState
    })
    if (onSelectNonRecurrentReservationDate) {
      onSelectNonRecurrentReservationDate(hour, restrictionIndex)
    }
  }

  return (
    <ModalGeneric
      modalTitle={<Icon name='RefreshClockSuccess' fill='#1F35B5' width={80} height={80} />}
      onCloseModal={onClose}
      hasButton
      btnOnClick={handlePrimaryButtonClick}
      btnText={confirmButtonText}
      btnDisabled={effectiveConfirmDisabled}
    >
      <S.PatientCreatingReservationRestrictionsModalContainer>
        <Typography weight='bold' center>
          {scheduleStartDate
            ? `Vamos reservar toda ${scheduleStartDate} para você, mas é preciso definir outros horários para os dias em que o psicólogo está indisponível:`
            : 'Para concluir a reserva recorrente, selecione horários alternativos para os dias com indisponibilidade:'}
        </Typography>

        <div>
          {parsedRestrictions.map((restrictionDate, index) => (
            <Typography key={`${restrictionDate.toISOString()}-${index}`} variant='content2' center>
              {format(restrictionDate, 'dd/MM/yyyy')}
            </Typography>
          ))}
        </div>

        <Typography weight='bold' variant='content2' center>
          Defina outros horários para estes dias
        </Typography>

        {isLoadingModalAvailabilities && (
          <S.SpinnerWrapper>
            <Spinner />
          </S.SpinnerWrapper>
        )}

        {hasRestrictions && !isLoadingModalAvailabilities && (
          <S.SelectorsContainer>
            {parsedRestrictions.map((restrictionDate, index) => {
              const weekDateOptions = getDateOptionsInRestrictionWeek(dateOptions, restrictionDate)
              const selectedDateKey = selectedOptionsByRestriction[index]?.dateKey
              const selectedHour = selectedOptionsByRestriction[index]?.hour
              const hasNoAvailableHoursInWeek = hasNoAvailableHoursByRestriction[index]
              const restrictionDateOption = {
                id: format(restrictionDate, 'yyyy-MM-dd'),
                label: format(restrictionDate, 'dd/MM'),
              }
              const dateSelectOptions = hasNoAvailableHoursInWeek
                ? [restrictionDateOption]
                : weekDateOptions
              const hourOptions = selectedDateKey
                ? (availabilityByDay[selectedDateKey] || []).map((hour) => ({
                    id: hour,
                    label: format(new Date(hour), 'HH:mm'),
                  }))
                : []

              const selectedDateOption = dateSelectOptions.find((option) => option.id === selectedDateKey) || null
              const selectedHourOption = hourOptions.find((option) => option.id === selectedHour) || null

              return (
                <S.SelectRow key={`restriction-row-${index}`}>
                  <S.DateSelectColumn>
                    {hasNoAvailableHoursInWeek ? (
                      <ExceptionDateAsText>
                        {format(restrictionDate, 'dd/MM')}
                      </ExceptionDateAsText>
                    ) : (
                      <Select
                        options={dateSelectOptions}
                        selected={selectedDateOption}
                        onSelect={(option) => handleSelectDate(String(option.id), index)}
                        label=''
                        customClassName='RestrictionDateSelect'
                      />
                    )}
                  </S.DateSelectColumn>
                  {!hasNoAvailableHoursInWeek && (
                    <S.HourSelectionSlot>
                      <Typography variant='content2' customClassName='Separator'>
                        às
                      </Typography>
                      <Select
                        options={hourOptions}
                        selected={selectedHourOption}
                        onSelect={(option) => handleSelectHour(String(option.id), index)}
                        label=''
                        customClassName='RestrictionHourSelect'
                        placeholder='Selecione'
                      />
                    </S.HourSelectionSlot>
                  )}
                  {hasNoAvailableHoursInWeek && (
                    <S.NoAvailabilityState>
                      <Typography variant='content2' customClassName='NoAvailabilityText'>
                        Sem horários. Prossiga e pule essa semana, ou mude o psicólogo.
                      </Typography>
                    </S.NoAvailabilityState>
                  )}
                </S.SelectRow>
              )
            })}
          </S.SelectorsContainer>
        )}

        <S.Footer>
          <Typography variant='content3' center>
            *Brasil (UTC−3) - Horário de Brasília Padrão 00h00 a 23h59  
          </Typography>
        </S.Footer>
      </S.PatientCreatingReservationRestrictionsModalContainer>
    </ModalGeneric>
  )
}
